"use client";
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import Image from "next/image";

import useWindowDimensions from "@/next-src/hooks/useWindowDimensions";
import convertHTMLtoJSX from "@/next-src/utils/convertHTMLtoJSX";

import comCSS from "./PlanFeatures.module.scss";
const STICKY_STATE = {
  FIXED: "FIXED",
  PINNED_BOTTOM: "PINNED_BOTTOM",
  SCROLL: "SCROLL",
};
const PlanFeatures = ({ className = "", data }) => {
  const { width: windowWidth } = useWindowDimensions();
  const [stickyState, setStickyState] = useState(STICKY_STATE.SCROLL);
  const wrapperRef = useRef();
  const { options, optionsTitles } = data || {};
  const everyPlanKey = ["essential", "optimal", "elite"];
  // types of cells 'safe', 'includes','special','text'

  // this is to show percent data animation. The code sets it only once, when a user scrolled to the section
  useEffect(() => {
    //after-hero-page-wrap-unique
    if (typeof window !== "undefined") {
      const onScroll = () => {
        if (!wrapperRef?.current) return;

        const { top, bottom } = wrapperRef.current.getBoundingClientRect();

        const isInView = top <= 50 && bottom >= 147;
        if (isInView) {
          setStickyState(STICKY_STATE.FIXED);
        } else if (bottom < 147) {
          setStickyState(STICKY_STATE.PINNED_BOTTOM);
        } else {
          setStickyState(STICKY_STATE.SCROLL);
        }
      };

      window.addEventListener("scroll", onScroll, false);
      window.addEventListener("resize", onScroll, false);

      // Call handler right away so state gets updated with initial window size
      onScroll();

      return () => {
        window.removeEventListener("scroll", onScroll, false);
        window.removeEventListener("resize", onScroll, false);
      };
    }
  }, []);

  return (
    <div
      ref={wrapperRef}
      className={cn(className, comCSS.wrapper, "background-color-soft-blue")}
    >
      <div className="padding-global">
        <div className="container-large">
          <div id="memberships" className="padding-section-large">
            <div className="programs-features-list">
              <div className="program-category">
                <div className={cn("programs-features-row is-sticky")}>
                  <div className="program-feature-name-wrap membership-grid-placeholder"></div>
                  <div className="feature-checks-wrap">
                    {optionsTitles?.map((el) => (
                      <div
                        id={el.id}
                        className="program-feature-wrap"
                        key={el.id}
                      >
                        <div className="membership-name">
                          {convertHTMLtoJSX(el.title)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="memberships-features-wrap">
                  {options
                    ?.filter((el) => !el.hide)
                    ?.map((el) => (
                      <div className="programs-features-row" key={el.id}>
                        <div id={el.id} className="program-feature-name-wrap">
                          <div className="feature-name">
                            {convertHTMLtoJSX(el.title)}
                          </div>
                          <div className="tooltip-wrap">
                            <span
                              className={cn(
                                "feature-tooltip-link w-inline-block",
                                comCSS.tooltipTrigger
                              )}
                            >
                              <Image
                                width="21"
                                height="21"
                                src="/images/home/63ee30c4612d1c99744331bb_info.svg"
                                loading="lazy"
                                alt=""
                                className="feature-tooltip-icon"
                              />
                            </span>
                            <div
                              className={cn(
                                comCSS.tooltipContent,
                                "tooltip-content shadow-large"
                              )}
                            >
                              <div>{convertHTMLtoJSX(el.tooltip)}</div>
                              <Image
                                width="24"
                                height="24"
                                src="/images/home/63ee3dfc612d1cd22a4409a5_tooltip-pointer.svg"
                                loading="lazy"
                                alt=""
                                className="tooltip-pointer"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="feature-checks-wrap">
                          {everyPlanKey.map((key) => (
                            <div
                              className="program-feature-wrap"
                              key={`feature-${key}-${el.id}`}
                            >
                              {el[key]?.type === "safe" && (
                                <Image
                                  width="24"
                                  height="24"
                                  src="/images/home/63f475134744d5babc6319cc_safe.svg"
                                  loading="lazy"
                                  alt=""
                                  className="icon-1x1-small"
                                />
                              )}
                              {el[key]?.type === "includes" && (
                                <Image
                                  width="24"
                                  height="24"
                                  src="/images/home/63e36c07240ed1bbb57cf916_green-check.svg"
                                  loading="lazy"
                                  alt=""
                                  className="icon-1x1-small"
                                />
                              )}
                              {el[key]?.type === "special" && (
                                <div className="pricing-small-tag">
                                  <div className="program-feature-text">
                                    {convertHTMLtoJSX(el[key]?.text)}
                                  </div>
                                </div>
                              )}
                              {el[key]?.type === "text" && (
                                <div
                                  className={cn("program-feature-text", {
                                    "is-empty": el[key]?.text === "–",
                                  })}
                                >
                                  {convertHTMLtoJSX(el[key]?.text)}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
            <div className="memberships-notes-wrap">
              {data?.notes?.map((el) => (
                <div
                  className="text-size-small text-color-slate-grey"
                  key={el.id}
                >
                  {convertHTMLtoJSX(
                    windowWidth > 992 ? el.text : el.textMobile
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

PlanFeatures.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape({
    preTitle: PropTypes.string,
    preTitleMobile: PropTypes.string,
    title: PropTypes.string,
    titleMobile: PropTypes.string,
    text: PropTypes.string,
    textMobile: PropTypes.string,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        tooltip: PropTypes.string,
        titleMobile: PropTypes.string,
        tooltipMobile: PropTypes.string,
        essential: PropTypes.shape({
          type: PropTypes.string,
          text: PropTypes.string,
        }),
        optimal: PropTypes.shape({
          type: PropTypes.string,
          text: PropTypes.string,
        }),
        elite: PropTypes.shape({
          type: PropTypes.string,
          text: PropTypes.string,
        }),
      })
    ),
    optionsTitles: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        titleMobile: PropTypes.string,
        id: PropTypes.string,
      })
    ),
    notes: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        text: PropTypes.string,
        textMobile: PropTypes.string,
      })
    ),
  }),
};

export default PlanFeatures;
